import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CATERING_CATEGORIES, OCCASIONS, CITIES, DIETS, Diet } from '../data/smaken';
import { usePlatform } from '../contexts/PlatformContext';
import { CateringCard } from '../components/CateringCard';
import { Reveal } from '../components/Reveal';
import { HowItWorks } from '../components/HowItWorks';
import { usePartnerSummaries, PartnerSelect, PartnerDetail } from '../components/CateringPartners';
import { Note, ArrowMark } from '../components/Marks';
import { useRequest } from '../CartContext';
const AREAS = Object.keys(CITIES);
export function Catering() {
  const [params, setParams] = useSearchParams();
  const {
    count
  } = useRequest();
  const {
    allCatering
  } = usePlatform();
  const diet = params.get('kost') ?? '';
  const category = params.get('kategori') ?? '';
  const occasion = params.get('tillfalle') ?? '';
  const area = params.get('ort') ?? '';
  const partner = params.get('verksamhet') ?? '';
  const partners = usePartnerSummaries();
  const activePartner = partners.find(p => p.slug === partner);
  const guests = params.get('gaster') ?? '';
  const query = params.get('q') ?? '';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const results = useMemo(() => {
    const guestCount = Number(guests);
    const q = query.trim().toLowerCase();
    return allCatering.filter(offer => {
      if (category && offer.category !== category) return false;
      if (diet && !offer.diets?.includes(diet as Diet)) return false;
      if (occasion && !offer.occasions.includes(occasion)) return false;
      if (area && offer.placeCity !== area) return false;
      if (partner && offer.placeSlug !== partner) return false;
      if (guestCount && offer.minGuests > guestCount) return false;
      if (!q) return true;
      return [offer.name, offer.description, offer.category, offer.placeName, ...offer.occasions].join(' ').toLowerCase().includes(q);
    });
  }, [allCatering, category, diet, occasion, area, partner, guests, query]);
  const chip = (active: boolean) => `border px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${active ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`;
  return <div className="mx-auto max-w-page px-5 py-12 md:px-8 md:py-16" data-id="element-807" data-mp="29qblhhclzsi5">
      <Reveal data-id="element-808" data-mp="29so13nuw9qs1">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-809" data-mp="2dsevs2njkr1u">
          <div data-id="element-810" data-mp="2dqbycxk27vn3">
            <h1 className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-811" data-mp="2irj6ixq46wua">Catering</h1>
            <Note className="mt-3 block" data-id="element-812" data-mp="2ipf3ei01i1v2">Vi vet vilka som kan leverera. Alla står vi för.</Note>
          </div>
          {count > 0 && <Link to="/forfragan" className="group flex items-center gap-2 bg-rod px-7 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-813" data-mp="ocyrb0kkbtia">
              Din förfrågan ({count})
              <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-814" data-mp="73wg2y1puu26" />
            </Link>}
        </div>
      </Reveal>

      {/* Filters — scroll away with the page so they never cover the list */}
      <div className="-mx-5 mt-10 border-b border-black/12 bg-papper px-5 md:-mx-8 md:px-8" data-id="element-815" data-mp="zsbkyrr4vvzs">
        <div className="space-y-5 border-t-2 border-black py-6" data-id="element-816" data-mp="uu9ydwui1ejk">
          <div data-id="element-817" data-mp="ptw2tea33x2f">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-818" data-mp="ptm7db69sxpc">Tillfälle</p>
            <div className="flex flex-wrap gap-2" data-id="element-819" data-mp="lsaij7v80gi7">
              <button onClick={() => setParam('tillfalle', '')} className={chip(!occasion)} data-id="element-820" data-mp="3akwfzblgryeu">
                Hela dagen
              </button>
              {OCCASIONS.map(o => <button key={o} onClick={() => setParam('tillfalle', occasion === o ? '' : o)} className={chip(occasion === o)} data-id="element-821" data-mp="36lnqwk1dcopm">
                  {o}
                </button>)}
            </div>
          </div>

          <div data-id="element-822" data-mp="oakncub6xqs9">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-823" data-mp="tbi73bbumsvv">Typ</p>
            <div className="flex flex-wrap gap-2" data-id="element-824" data-mp="1h12xuge8uufp">
              <button onClick={() => setParam('kategori', '')} className={chip(!category)} data-id="element-825" data-mp="1h1m7zaxnkej6">
                Allt
              </button>
              {CATERING_CATEGORIES.map(c => <button key={c} onClick={() => setParam('kategori', category === c ? '' : c)} className={chip(category === c)} data-id="element-826" data-mp="17zd0yv1lf3aj">
                  {c}
                </button>)}
              <span className="mx-1 hidden w-px self-stretch bg-black/15 sm:block" aria-hidden="true" data-id="element-827" data-mp="mkyy11cqcgu" />
              {DIETS.map(d => <button key={d} onClick={() => setParam('kost', diet === d ? '' : d)} className={chip(diet === d)} data-id="element-828" data-mp="3p7eln6tsrgrj">
                  {d}
                </button>)}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-6 pt-1" data-id="element-829" data-mp="3u7j4v4m3aplg">
            <PartnerSelect partners={partners} selected={partner} onSelect={slug => setParam('verksamhet', slug)} data-id="element-830" data-mp="36i7ztaxpi91i" />
            <div data-id="element-831" data-mp="31kyd431pcbhd">
              <label htmlFor="c-guests" className="mb-2 block text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-832" data-mp="31hajl3w9bp2n">
                Antal gäster
              </label>
              <input id="c-guests" type="number" min="1" value={guests} onChange={e => setParam('gaster', e.target.value)} placeholder="25" className="w-28 border-b border-black/25 bg-transparent pb-2 font-display text-2xl outline-none transition-colors duration-150 ease-out placeholder:text-black/25 focus:border-rod" data-id="element-833" data-mp="3bg01gwo5l9iw" />
            </div>
            <div data-id="element-834" data-mp="3ha4ifdjffo61">
              <label htmlFor="c-area" className="mb-2 block text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-835" data-mp="3h9mpoqvz3s2v">
                Levereras i
              </label>
              <select id="c-area" value={area} onChange={e => setParam('ort', e.target.value)} className="cursor-pointer border-b border-black/25 bg-transparent pb-2 font-display text-2xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-836" data-mp="2piozbth2wjkk">
                <option value="" data-id="element-837" data-mp="33izn2nn3di1i">Hela Småland</option>
                {AREAS.map(a => <option key={a} data-id="element-838" data-mp="ds4hujg7al2g">{a}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {activePartner && <PartnerDetail partner={activePartner} data-id="element-839" data-mp="239rlwr90b843" />}

      <p className="mt-8 text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-840" data-mp="23bktq9vcvydo">
        {results.length} alternativ
      </p>

      {results.length === 0 ? <div className="mt-6 border-y-2 border-black py-20 text-center" data-id="element-841" data-mp="1tids903ql2iv">
          <p className="display-xl text-3xl uppercase" data-id="element-842" data-mp="1ol55em7n8aba">Inget matchar. Än.</p>
          <p className="mt-3 text-black/60" data-id="element-843" data-mp="1kj0i057sg9x3">
            Justera antalet gäster eller släpp ett filter — vi har mer än det ser ut som.
          </p>
        </div> : <div className="mt-6 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-844" data-mp="1fm0b5s34734x">
          {results.map(offer => <CateringCard key={offer.id} offer={offer} data-id="element-845" data-mp="15lrbg49wyzks" />)}
        </div>}

      <HowItWorks data-id="element-846" data-mp="15o42r81kamai" />

      {/* Always one tap from finishing the order, wherever you are in the list */}
      {count > 0 && <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-5 pb-5" data-id="element-847" data-mp="2zz92w2dfwgl3">
          <Link to="/forfragan" className="group pointer-events-auto flex items-center gap-3 bg-rod px-7 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper shadow-lg shadow-black/20 transition-colors duration-150 ease-out hover:bg-black" data-id="element-848" data-mp="28u4yyvivuykg">
            Gå vidare med {count} {count === 1 ? 'val' : 'val'}
            <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-849" data-mp="241lm60s0s7gp" />
          </Link>
        </div>}
    </div>;
}